import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, redirect } from "@tanstack/react-router";
import { Filter, Video, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { z } from "zod";

import {
  SummitCalendarMenu,
  SummitSessionCard,
  ICalCalendarIcon,
  SpeakerAvatarStack,
  UpNextForYou,
  SummitLifetimeAccessBanner,
} from "~/components/summit";
import { SummitDayAccordion } from "~/components/summit";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { getCDNImageUrl } from "~/components/ui/cdn-image";
import { CDNImage } from "~/components/ui/cdn-image";
import { CtaBanner } from "~/components/ui/cta-banner";
import { Footer } from "~/components/ui/footer";
import ImageDcmLogoHorizontalWhiteUrl from "~/img/logo/dcm-logo-horizontal-white.png";
import { cn } from "~/lib/cn";
import {
  getSummitDataByIdOptions,
  registerForSession,
  unregisterFromSession,
} from "~/lib/functions/summit";
import { getUserPasswordStatus } from "~/lib/functions/user-security";
import { createBrandMeta } from "~/lib/shared/meta";
import type { SessionFooterVariant } from "~/lib/summit";
import {
  SUMMIT_VIP_UPSELL_URL,
  SUMMIT_WORKSHOP_REGISTRATION_LIMIT,
} from "~/lib/summit";
import { formatSummitDateRange } from "~/lib/summit";
import { computeSummitTiming, getDefaultOpenDays } from "~/lib/summit";

const AgendaSearchSchema = z
  .object({
    am_id: z.string().optional(),
    live_id: z.string().optional(),
    session_id: z.string().optional(),
  })
  .passthrough();

export const Route = createFileRoute("/summit/$summitId/agenda")({
  params: {
    parse: (params) => ({
      summitId: z.string().uuid().parse(params.summitId),
    }),
    stringify: (params) => ({ summitId: params.summitId }),
  },
  validateSearch: (search) => AgendaSearchSchema.parse(search ?? {}),
  beforeLoad: async () => {
    // Custom auth check - redirect to /summit/register instead of /signin
    const { fetchClerkAuth } = await import("~/routes/__root");
    const { userId } = await fetchClerkAuth();

    if (!userId) {
      throw redirect({ to: "/summit/register" });
    }
  },
  loader: async ({ context, params }) => {
    const summitData = await context.queryClient.ensureQueryData(
      getSummitDataByIdOptions(params.summitId),
    );

    if (!summitData.userRegistration.isRegistered) {
      throw redirect({
        to: "/summit/register",
      });
    }

    const passwordStatus = await getUserPasswordStatus();

    if (!passwordStatus.hasPassword) {
      const redirectSearch: Record<string, string> = {
        redirectTo: `/summit/${params.summitId}/agenda`,
        message:
          "You need to set a password first to ensure you will keep having access.",
      };

      throw redirect({
        to: "/settings/password",
        search: () => redirectSearch,
        replace: true,
      });
    }

    return summitData;
  },
  head: ({ loaderData, params }) => {
    const summitData = loaderData;

    if (!summitData) {
      return createBrandMeta({
        title: "Summit - Join Expert Coaching Sessions",
        description:
          "Join industry leaders and master coaches for an immersive learning experience that will transform your coaching practice.",
        ogType: "website",
      });
    }

    // Generate keywords from session topics
    const allTopics = new Set<string>();
    summitData.days.forEach((day) => {
      day.sessions.forEach((session) => {
        (session.topics || []).forEach((topic) => allTopics.add(topic));
      });
    });

    const keywords = [
      "summit",
      "coaching",
      "professional development",
      "master coaches",
      "industry leaders",
      ...Array.from(allTopics),
    ];

    // Create summit description with session count
    const totalSessions = summitData.days.reduce(
      (count, day) => count + day.sessions.length,
      0,
    );
    const description = `Join industry leaders and master coaches for ${summitData.summit.title}. ${totalSessions} sessions covering ${Array.from(allTopics).slice(0, 3).join(", ")} and more. Transform your coaching practice with expert insights.`;

    return createBrandMeta({
      title: summitData.summit.title,
      description,
      ogTitle: `${summitData.summit.title} | Dream Coach Match`,
      ogDescription: description,
      ogImage: getCDNImageUrl(
        "https://form-data.dreamcoachmatch.com/summit/summit-bg.jpg",
        1200,
      ),
      ogType: "website",
      ogUrl: `https://dreamcoachmatch.com/summit/${params.summitId}/agenda`,
      twitterCard: "summary_large_image",
      twitterTitle: summitData.summit.title,
      twitterDescription: description,
      twitterImage: getCDNImageUrl(
        "https://form-data.dreamcoachmatch.com/summit/summit-bg.jpg",
        1200,
      ),
      canonical: `https://dreamcoachmatch.com/summit/${params.summitId}/agenda`,
      keywords,
      author: "Dream Coach Match",
    });
  },
  component: SummitAgendaPage,
});

import { YouTubeLiveEmbed } from "~/components/summit/youtube-live-embed";
import { useActiveLiveStream } from "~/lib/summit";
import { useLiveSummitCTA } from "~/lib/summit";

interface LiveStreamCardProps {
  videoId: string;
  /** Hide chat sidebar (e.g., for replay mode) */
  hideChat?: boolean;
}

function LiveStreamCard({
  videoId,
  hideChat,
}: LiveStreamCardProps): React.ReactElement {
  // Use live-polling CTA hook so admin changes appear within 30 seconds
  const activeCTA = useLiveSummitCTA();

  return (
    <div className="mx-auto mt-6 max-w-7xl px-4 md:px-6">
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg">
        <YouTubeLiveEmbed videoId={videoId} hideChat={hideChat} />
        {/* CTA button - appears at scheduled time, polls server every 30s */}
        {activeCTA && (
          <div className="flex flex-wrap justify-center gap-3 border-t border-gray-200 bg-gray-50 p-4">
            <Button
              variant="default"
              size="lg"
              onClick={() => window.open(activeCTA.url, "_blank")}
            >
              {activeCTA.text}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function SummitAgendaPage() {
  const { summitId } = Route.useParams();
  const { live_id: liveIdOverride, session_id: sessionIdParam } =
    Route.useSearch();
  const queryClient = useQueryClient();
  const {
    data: summitData,
    isLoading,
    error,
  } = useQuery(getSummitDataByIdOptions(summitId));
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [mutationError, setMutationError] = useState<string | null>(null);

  // Compute summit timing info (phase and day statuses)
  const summitTiming = useMemo(() => {
    if (!summitData) return null;
    return computeSummitTiming(summitData.days);
  }, [summitData]);

  // Compute default open days based on summit timing
  const defaultOpenDays = useMemo(() => {
    if (!summitData || !summitTiming) return [];
    return getDefaultOpenDays(summitData.days, summitTiming);
  }, [summitData, summitTiming]);

  const [openDays, setOpenDays] = useState<string[]>([]);
  const [hasInitializedOpenDays, setHasInitializedOpenDays] = useState(false);

  // Initialize open days once when data loads
  useEffect(() => {
    if (!hasInitializedOpenDays && defaultOpenDays.length > 0) {
      setOpenDays(defaultOpenDays);
      setHasInitializedOpenDays(true);
    }
  }, [defaultOpenDays, hasInitializedOpenDays]);

  const registerMutation = useMutation({
    mutationFn: registerForSession,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["summit", summitId] });
      setMutationError(null);
    },
    onError: (error) => {
      const message =
        error instanceof Error
          ? error.message
          : "We couldn't register you for that workshop. Please try again.";
      setMutationError(message);
    },
  });

  const unregisterMutation = useMutation({
    mutationFn: unregisterFromSession,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["summit", summitId] });
      setMutationError(null);
    },
    onError: (error) => {
      const message =
        error instanceof Error
          ? error.message
          : "We couldn't update your registration right now. Please try again.";
      setMutationError(message);
    },
  });

  // Days are collapsed by default (empty openDays array)

  // Auto-expand day and scroll to session when session_id is in URL
  useEffect(() => {
    if (!sessionIdParam || !summitData) return;

    // Find which day contains this session
    const dayWithSession = summitData.days.find((day) =>
      day.sessions.some((session) => session.id === sessionIdParam),
    );

    if (!dayWithSession) return;

    // Expand the day if not already open
    setOpenDays((prev) =>
      prev.includes(dayWithSession.id) ? prev : [...prev, dayWithSession.id],
    );

    // Scroll to session after accordion has time to expand
    setTimeout(() => {
      const element = document.getElementById(`session-${sessionIdParam}`);
      element?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 350);
  }, [sessionIdParam, summitData]);

  if (isLoading) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <div className="text-gray-600">Loading summit...</div>
      </div>
    );
  }

  if (error || !summitData) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <div className="text-red-600">Error loading summit data</div>
      </div>
    );
  }

  const getFooterVariant = (
    session: (typeof summitData.days)[0]["sessions"][0],
  ): SessionFooterVariant => {
    // Use footerVariants from server if available, otherwise create default
    return summitData?.footerVariants?.[session.id] || { type: "no-cta" };
  };

  // Flatten all sessions for live stream detection
  const allSessions = useMemo(() => {
    if (!summitData) return [];
    return summitData.days.flatMap((day) => day.sessions);
  }, [summitData]);

  // Detect active live stream from session data
  const activeLiveStream = useActiveLiveStream(allSessions);

  // Use override if provided, otherwise use detected active stream
  const activeVideoId = liveIdOverride ?? activeLiveStream?.videoId ?? null;
  // In replay mode (stream ended), hide chat since it's no longer active
  const isReplay = liveIdOverride
    ? false
    : (activeLiveStream?.isReplay ?? false);

  // Get all unique tags from all days
  const allTags = useMemo(() => {
    const tags = new Set<string>();
    summitData?.days.forEach((day) => {
      day.sessions.forEach((session) => {
        (session.topics || []).forEach((topic) => tags.add(topic));
      });
    });
    return Array.from(tags).sort();
  }, [summitData?.days]);

  // Filter sessions based on selected tags
  const filteredDays = useMemo(() => {
    if (!summitData) return [];
    if (selectedTags.length === 0) return summitData.days;

    return summitData.days
      .map((day) => ({
        ...day,
        sessions: day.sessions.filter((session) =>
          (session.topics || []).some((topic) => selectedTags.includes(topic)),
        ),
      }))
      .filter((day) => day.sessions.length > 0);
  }, [selectedTags, summitData]);

  const toggleTag = (tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag],
    );
  };

  const clearFilters = () => {
    setSelectedTags([]);
  };

  const handleRegister = async (sessionId: string) => {
    await registerMutation.mutateAsync({ data: { sessionId } });
  };

  const handleCancelRegistration = async (sessionId: string) => {
    await unregisterMutation.mutateAsync({ data: { sessionId } });
  };

  const isVip = summitData?.userRegistration?.isVip ?? false;
  const workshopRegistrationsCount =
    summitData?.userRegistration?.workshopRegistrationsCount ?? 0;
  const workshopLimit =
    summitData?.userRegistration?.workshopRegistrationLimit ??
    SUMMIT_WORKSHOP_REGISTRATION_LIMIT;
  const vipUpsellUrl = summitData.vipUpsellUrl ?? SUMMIT_VIP_UPSELL_URL;

  const summitDateRange =
    summitData.summit.startDate && summitData.summit.endDate
      ? formatSummitDateRange(
          summitData.summit.startDate,
          summitData.summit.endDate,
        )
      : null;

  // Compute days with their event times for calendar events
  const summitDaysForCalendar = useMemo(() => {
    return summitData.days.map((day) => {
      let earliestStart: Date | null = null;
      let latestEnd: Date | null = null;

      for (const session of day.sessions) {
        const start = new Date(session.startTimeUtc);
        const end = new Date(
          start.getTime() + session.durationMinutes * 60 * 1000,
        );

        if (!earliestStart || start < earliestStart) earliestStart = start;
        if (!latestEnd || end > latestEnd) latestEnd = end;
      }

      return {
        id: day.id,
        date: day.date,
        title: day.title,
        eventStartUtc: earliestStart ?? undefined,
        eventEndUtc: latestEnd ?? undefined,
      };
    });
  }, [summitData.days]);

  return (
    <div className="min-h-dvh">
      {/* Header with Background Image */}
      <div className="px-4 md:my-8 md:px-6">
        <div className="relative w-full overflow-hidden rounded-xl shadow-lg shadow-purple-800/20 md:shadow-2xl">
          <CDNImage
            src="https://form-data.dreamcoachmatch.com/summit/summit-bg.jpg"
            alt=""
            width={2400}
            className="absolute inset-0 h-full w-full object-cover"
            loading="eager"
            priority
            aria-hidden="true"
          />
          <div className="relative z-10 bg-purple-900/60 px-4 py-12 text-center md:px-6 md:py-16 lg:px-8">
            <img
              src={ImageDcmLogoHorizontalWhiteUrl}
              alt="Dream Coach Match"
              className="mx-auto mb-4 h-5 w-auto opacity-80 md:mb-8 md:h-8"
            />
            <h1 className="mb-2 font-heading text-3xl tracking-tight text-white md:mb-4 md:text-5xl lg:text-6xl">
              {summitData.summit.title}
            </h1>
            {summitDateRange && (
              <p className="text-xs font-medium text-purple-200 md:text-sm">
                {summitDateRange}
              </p>
            )}
            {summitData.summit.description && (
              <p className="mx-auto mt-4 max-w-2xl text-base text-purple-100 md:mt-8 md:text-lg">
                {summitData.summit.description}
              </p>
            )}
            <div className="mt-6 flex justify-center md:mt-8">
              <SummitCalendarMenu
                summit={
                  summitData?.summit
                    ? { ...summitData.summit, days: summitDaysForCalendar }
                    : null
                }
                buttonClassName="gap-2 bg-white/10 hover:bg-white/20 text-white border-white/20 backdrop-blur-sm"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Live Stream Card - shown when live_id param is set or active live stream detected */}
      {activeVideoId && (
        <LiveStreamCard videoId={activeVideoId} hideChat={isReplay} />
      )}

      {/* Summit Container */}
      <div className="mx-auto mt-6 max-w-5xl px-4 md:px-6">
        {/* Content */}
        <div>
          {/* VIP Upsell Banner - Shown for Non-VIPs */}
          {!isVip && (
            <SummitLifetimeAccessBanner
              className="mb-4 md:mb-6"
              vipUpsellUrl={vipUpsellUrl}
            />
          )}
          {mutationError && (
            <div className="mb-4 rounded-md border border-red-300 bg-red-50 p-3 text-red-800 md:mb-6">
              {mutationError}
            </div>
          )}

          {/* Up Next For You - shows registered sessions about to start */}
          <UpNextForYou
            sessions={allSessions}
            footerVariants={summitData.footerVariants ?? {}}
          />

          {/* Filters */}
          {allTags.length > 0 && (
            <div className="mb-6 rounded-xl border border-gray-200 bg-white p-3 shadow-sm md:mb-8 md:p-6">
              <div className="mb-2 flex items-center justify-between md:mb-3">
                <div className="flex items-center gap-2 text-gray-900">
                  <Filter className="h-3.5 w-3.5 md:h-4 md:w-4" />
                  <span className="text-sm font-medium">Filter by Topics</span>
                </div>
                {selectedTags.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearFilters}
                    className="h-auto p-1"
                  >
                    Clear all
                  </Button>
                )}
              </div>
              <div className="flex flex-wrap gap-1.5 md:gap-2">
                {allTags.map((tag) => (
                  <Badge
                    key={tag}
                    variant={selectedTags.includes(tag) ? "default" : "outline"}
                    className={cn(
                      "cursor-pointer px-2 py-0.5 text-xs transition-colors md:px-3 md:py-1 md:text-sm",
                      selectedTags.includes(tag)
                        ? "bg-primary text-primary-foreground hover:bg-primary/90"
                        : "border-gray-300 text-gray-700 hover:border-gray-400 hover:bg-gray-50",
                    )}
                    onClick={() => toggleTag(tag)}
                  >
                    {tag}
                    {selectedTags.includes(tag) && (
                      <X className="ml-1 h-2.5 w-2.5 md:h-3 md:w-3" />
                    )}
                  </Badge>
                ))}
              </div>
              <p className="mt-3 text-xs text-gray-600 md:text-sm">
                You're registered for {workshopRegistrationsCount}/
                {workshopLimit} workshops. Choose up to {workshopLimit} in
                total.
              </p>
            </div>
          )}

          {/* Summit Days Accordion */}
          <div className="mb-6 md:mb-8">
            <SummitDayAccordion.Root
              type="multiple"
              value={openDays}
              onValueChange={setOpenDays}
              className="gap-6 md:gap-8"
            >
              {filteredDays.map((day) => {
                const isExpanded = openDays.includes(day.id);
                const dayStatus =
                  summitTiming?.getDayStatus(day.id) ?? "future";
                const isPastDay = dayStatus === "past";
                const daySpeakers = day.sessions.map((session) => ({
                  id: session.speaker.id,
                  name: session.speaker.name,
                  avatarUrl: session.speaker.avatarUrl,
                  sessionTitle: session.title,
                  sessionId: session.id,
                }));

                const handleSpeakerClick = (sessionId: string) => {
                  // Expand the day if collapsed
                  if (!isExpanded) {
                    setOpenDays((prev) => [...prev, day.id]);
                  }
                  // Scroll to session after a brief delay to allow accordion to open
                  setTimeout(
                    () => {
                      const element = document.getElementById(
                        `session-${sessionId}`,
                      );
                      element?.scrollIntoView({
                        behavior: "smooth",
                        block: "center",
                      });
                    },
                    isExpanded ? 0 : 350,
                  );
                };

                return (
                  <SummitDayAccordion.Item key={day.id} value={day.id}>
                    <SummitDayAccordion.Trigger>
                      <div className="flex flex-1 items-center gap-4">
                        <ICalCalendarIcon
                          date={day.date}
                          className="h-20 w-16 shrink-0 md:h-24 md:w-20"
                          muted={isPastDay && !isExpanded}
                        />
                        <div className="flex flex-col gap-1 text-left md:gap-2">
                          {/* Speaker avatars - shown when collapsed */}
                          {!isExpanded && daySpeakers.length > 0 && (
                            <SpeakerAvatarStack
                              speakers={daySpeakers}
                              maxVisible={10}
                              size="sm"
                              onSpeakerClick={handleSpeakerClick}
                              className="mb-1"
                            />
                          )}
                          <h2
                            className={cn(
                              "font-heading text-2xl tracking-tight md:text-4xl",
                              isPastDay && !isExpanded
                                ? "text-gray-400"
                                : "text-gray-900",
                            )}
                          >
                            {day.title}
                          </h2>
                          {day.description && (
                            <p className="text-text-secondary text-xs md:text-sm">
                              {day.description}
                            </p>
                          )}
                          {!isExpanded && (
                            <span className="mt-1 inline-flex cursor-pointer items-center gap-1.5 text-xs font-medium text-brand-600 md:text-sm">
                              Show {day.sessions.length} session
                              {day.sessions.length !== 1 ? "s" : ""}
                            </span>
                          )}
                        </div>
                      </div>
                    </SummitDayAccordion.Trigger>
                    <SummitDayAccordion.Content className="p-3 md:p-6">
                      {/* Day Actions */}
                      {day.liveUrl && (
                        <div className="mb-3 flex flex-wrap gap-2 border-b border-gray-200 pb-2 md:mb-6 md:gap-3 md:pb-4">
                          <Button
                            size="sm"
                            onClick={() => window.open(day.liveUrl, "_blank")}
                            className="gap-2"
                          >
                            <Video className="h-4 w-4" />
                            Join Live
                          </Button>
                        </div>
                      )}

                      {/* Session Cards Grid */}
                      {day.sessions.length === 0 ? (
                        <div className="rounded-lg border border-gray-200 bg-gray-50 p-6 text-center">
                          <p className="text-sm text-gray-600">
                            No sessions scheduled yet for this day. Check back
                            soon!
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-3 md:space-y-4">
                          {day.sessions.map((session) => (
                            <SummitSessionCard
                              key={session.id}
                              session={session}
                              summitId={summitData.summit.id}
                              footerVariant={getFooterVariant(session)}
                              userIsVip={
                                summitData?.userRegistration?.isVip || false
                              }
                              vipUpsellUrl={vipUpsellUrl}
                              onRegister={() => handleRegister(session.id)}
                              onCancelRegistration={() =>
                                handleCancelRegistration(session.id)
                              }
                            />
                          ))}
                        </div>
                      )}
                    </SummitDayAccordion.Content>
                  </SummitDayAccordion.Item>
                );
              })}
            </SummitDayAccordion.Root>
          </div>

          {filteredDays.length === 0 && (
            <div className="rounded-lg border border-gray-200 bg-white p-6 text-center shadow-sm md:p-12">
              <p className="text-base text-gray-900 md:text-lg">
                No sessions match your selected filters.
              </p>
              <Button onClick={clearFilters} className="mt-4">
                Clear Filters
              </Button>
            </div>
          )}

          {/* VIP Upsell Bottom Section - Shown for Non-VIPs */}
          {!isVip && (
            <SummitLifetimeAccessBanner
              className="mt-6 md:mt-8"
              variant="compact"
              vipUpsellUrl={vipUpsellUrl}
            />
          )}

          {/* Discovery CTA */}
          <div className="mt-6 md:mt-8">
            <CtaBanner
              headline="Ready to find your dream coach?"
              description="Answer a few questions and get matched with coaches tailored to your needs, goals, and preferences."
              buttonHref="/discovery?utm_campaign=summit_agenda_bottom&utm_source=summit_agenda"
              buttonText="Start Discovery"
            >
              <div className="mt-4">
                <Link
                  to="/page/$name"
                  params={{ name: "member" }}
                  className="my-2 text-center text-xs text-purple-200/70 underline transition-colors hover:text-white hover:no-underline"
                >
                  How does Dream Coach Match work?
                </Link>
              </div>
            </CtaBanner>
          </div>
        </div>
      </div>
      <div className="mt-12 md:mt-16">
        <Footer />
      </div>
    </div>
  );
}
